import React, { useEffect, useState } from 'react';
import { Col, Input, Row, Select } from 'antd';
import { useIntl } from 'react-intl';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { Country } from 'entities/countries/model/types';

export type PhoneInputState = {
  countryCode: number | undefined;
  phoneNumber: string;
  haveErrors: boolean;
};

type PhoneInputProps = {
  countries: Country[];
  onChange: (state: PhoneInputState) => unknown;
  forceShowErrors?: boolean;
  value?: PhoneInputState;
};

const PhoneInput: React.FC<PhoneInputProps> = ({ countries, onChange, forceShowErrors = false, value }) => {
  const { formatMessage: t } = useIntl();

  const [state, setState] = useState<PhoneInputState>(() => {
    if (value) {
      return {
        countryCode: value.countryCode,
        phoneNumber: value.phoneNumber,
        haveErrors: value.haveErrors ?? true,
      };
    }
    return {
      countryCode: undefined,
      phoneNumber: '',
      haveErrors: true,
    };
  });

  const [errors, setErrors] = useState({
    country: '',
    phone: ''
  });

  const [touched, setTouched] = useState({
    country: false,
    phone: false
  });

  const validate = (showErrors: boolean = false) => {
    let countryError = '';
    let phoneError = '';
    let hasCountryError = false;
    let hasPhoneError = false;

    const country = countries.find((c) => c.id === state.countryCode);
    if (!country) {
      hasCountryError = true;
      countryError = showErrors ? t({ id: 'schemas.validation.phone.country.required' }) : '';
    }

    const phoneNumber = parsePhoneNumberFromString(
      `+${country?.phoneCode || ''}${state.phoneNumber}`
    );

    if (!phoneNumber || !phoneNumber.isValid()) {
      hasPhoneError = true;
      phoneError = showErrors ? t({ id: 'schemas.validation.phone.invalid' }) : '';
    }

    const haveErrors = hasCountryError || hasPhoneError;

    setErrors({ country: countryError, phone: phoneError });
    onChange({ ...state, haveErrors });
  };

  // Синхронизация с внешним значением
  useEffect(() => {
    if (value && (value.countryCode !== state.countryCode || value.phoneNumber !== state.phoneNumber)) {
      setState({
        countryCode: value.countryCode,
        phoneNumber: value.phoneNumber,
        haveErrors: value.haveErrors ?? true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.countryCode, value?.phoneNumber]);

  useEffect(() => {
    const shouldShowErrors = forceShowErrors || touched.country || touched.phone;
    validate(shouldShowErrors);
  }, [state.countryCode, state.phoneNumber, touched.country, touched.phone, forceShowErrors]);

  const filterOption = (input: string, option?: { label: string; value: number; children?: React.ReactNode }) => {
    if (!option) return false;
    const searchText = input.toLowerCase();
    const country = countries.find(c => c.id === option.value);
    if (!country) return false;
    
    return (
      country.name.toLowerCase().includes(searchText) ||
      country.phoneCode.includes(searchText) ||
      `+${country.phoneCode}`.includes(searchText)
    );
  };

  const shouldShowErrors = forceShowErrors || touched.country || touched.phone;
  const showCountryError = shouldShowErrors && !!errors.country;
  const showPhoneError = shouldShowErrors && !!errors.phone;

  return (
    <Row gutter={8}>
      <Col span={10}>
        <Select
          value={state.countryCode}
          onChange={(v) => {
            setState((old) => ({ ...old, countryCode: v }));
            setTouched((old) => ({ ...old, country: true }));
          }}
          onBlur={() => setTouched((old) => ({ ...old, country: true }))}
          style={{ height: 55, textAlign: 'left' }}
          showSearch
          filterOption={filterOption}
          optionLabelProp="label"
          placeholder="+000"
          allowClear={false}
          placement="bottomLeft"
          dropdownMatchSelectWidth={false}
          dropdownStyle={{ textAlign: 'left' }}
          listHeight={300}
          status={showCountryError ? 'error' : undefined}
        >
          {countries.map((country) => (
            <Select.Option
              key={country.id}
              value={country.id}
              label={`+${country.phoneCode}`}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <div
                  className={`fi fi-${country.iso2.toLowerCase()}`}
                  style={{ marginRight: 6, flex: 'none' }}
                />
                <div>
                  {`+${country.phoneCode} ${country.name}`}
                </div>
              </div>
            </Select.Option>
          ))}
        </Select>
        {showCountryError && (
          <div style={{ color: '#ff4d4f', marginTop: 4 }}>{errors.country}</div>
        )}
      </Col>

      <Col span={14}>
        <Input
          style={{ height: 55 }}
          placeholder="000-00-00"
          value={state.phoneNumber}
          onChange={(e) => {
            setState((old) => ({ ...old, phoneNumber: e.target.value }));
            setTouched((old) => ({ ...old, phone: true }));
          }}
          onBlur={() => setTouched((old) => ({ ...old, phone: true }))}
          status={showPhoneError ? 'error' : undefined}
        />
        {showPhoneError && (
          <div style={{ color: '#ff4d4f', marginTop: 4, paddingLeft: 50}}>{errors.phone}</div>
        )}
      </Col>
    </Row>
  );
};

export default PhoneInput;
